import { useQuery } from '@tanstack/react-query'
import axios, { AxiosError } from 'axios'
import { useSelector } from 'react-redux'
import { CustomQueryOptions } from 'src/queries/queries.utils'
import { selectAuthedJsonApiRequestConfig } from 'src/redux/selectors/axios.selectors'
import { BackendErrorPayload } from 'src/utils/error.utils'

export type GroupSSOConfig = {
  /** PK in database */
  id: string
  /** Organization Id */
  groupId: string
  /** type of SSO - one of SAML or OIDC */
  type: 'SAML' | 'OIDC'
  /** Name of the provider (like Gsuite or Okta) */
  providerName: string

  /** ISO8601 Timestamp of when this configuration was created */
  createdAt: string
  /** `true` if SSO is enabled for all users of the organization */
  enabled: boolean
  /** All of the SAML metadata */
  samlMetadata: {
    entityId: string
    acsUrl: string
    idpSsoUrl: string
    idpIssuer: string
    x509CertificatePem: string
  } | null
}
/**
 * Fetches the current user's organization SSO configuration
 * If no configuration exists, internally a 404 error will be handled and a blank
 * SSO configuration object will be created
 * @param organizationId
 * @param options
 * @returns
 */
export function useGetGroupSsoConfig(
  organizationId?: string,
  options?: CustomQueryOptions<GroupSSOConfig>,
) {
  const authedConfig = useSelector(selectAuthedJsonApiRequestConfig)

  return useQuery<GroupSSOConfig, AxiosError<BackendErrorPayload<string>>>({
    ...options,
    queryKey: ['organizations', organizationId, 'settings', 'sso'],
    queryFn: async () => {
      try {
        const response = await axios.get(
          `organizations/${organizationId}/settings/sso`,
          authedConfig,
        )
        return response.data as GroupSSOConfig
      } catch (error) {
        // Return default empty config on 404
        if (axios.isAxiosError(error) && error.response?.status === 404) {
          return {
            id: '',
            groupId: organizationId || '',
            // TODO: Make this variable and add a
            // piece of UI on OrganizationSSOConfig.tsx to allow for
            // swapping between OIDC and SAML.
            // At this time, we only support SAML so let's just hardcode it
            // and assume it's always SAML
            type: 'SAML',
            providerName: '',
            createdAt: '',
            enabled: false,
            samlMetadata: {
              entityId: '',
              acsUrl: '',
              idpSsoUrl: '',
              idpIssuer: '',
              x509CertificatePem: '',
            },
          } as GroupSSOConfig
        }
        throw error
      }
    },

    enabled: !!(organizationId && options?.enabled),
  })
}
